import { PopupHeader, BroadsiderHudPopup, IconButton, AssetView } from '../';
import {
  StyledItemEditor,
  EditorContent,
  EditorLabel,
  ItemDataSection,
  ItemParams,
  ItemParam,
  ItemList,
  ItemListWrapper,
  ItemAssets,
  ItemAsset,
  InternalItem,
  InternalItemInfo
} from './ItemEditorPopup.styled';
import { TextInput, TextInputMultiline } from '../../components';
import { useMemo, useEffect, useState, useContext } from 'react';
import { getEditorItems, updateEditorItem } from '../../providers/BasejumpApiProvider';
import { AppContext } from '../../providers/AppContext';
import FigureModel from "../../models/FigureModel";
import AssetModel from "../../models/AssetModel";
import MintFigureView from "../../components/MintActionFigures/MintFigureView/MintFigureView";
import { useTokenAuth } from '../../hooks';
import { TabHeader, Tab } from './Tabs';
import { Metrics } from './Metrics';
import { NpcEditor } from './NpcEditor';

const AllItemList = ({ items, onSelectItem, selectedItem }) => {
  return (
    <ItemList>
      <ItemListWrapper>
        {items.map((item, index) => {
          return (
            <InternalItem $selected={selectedItem && selectedItem.getItemId() === item.getItemId()} onClick={() => onSelectItem(item)}>
              <InternalItemInfo>{item.getName()} ({item.getRarity()})</InternalItemInfo>
            </InternalItem>
          )
	})}
      </ItemListWrapper>
    </ItemList>
  );
};

const validateUpdateObject = (updateObject) => {
  if (!updateObject) {
    throw new Error('No update object!');
  }
  if (!updateObject.name) {
    throw new Error('Name cannot be empty!');
  }
  if (!updateObject.description) {
    throw new Error('Description cannot be empty!');
  }
  if (!updateObject.stats) {
    throw new Error('Stats cannot be empty!');
  }
  if (!updateObject.stats.attack_points && updateObject.stats.attack_points !== 0) {
    throw new Error('Attack Points cannot be empty!');
  }
  if (!updateObject.stats.defence_points && updateObject.stats.defence_points !== 0) {
    throw new Error('Defense Points cannot be empty!');
  }
  if (!updateObject.stats.health_points && updateObject.stats.health_points !== 0) {
    throw new Error('Health Points cannot be empty!');
  }
  if (!updateObject.stats.speed_points && updateObject.stats.speed_points !== 0) {
    throw new Error('Speed Points cannot be empty!');
  }
  if (!updateObject.stats.critical_chance_points && updateObject.stats.critical_chance_points !== 0) {
    throw new Error('Critical Chance Points cannot be empty!');
  }
  if (!updateObject.stats.attack_multiplier && updateObject.stats.attack_multiplier !== 0) {
    throw new Error('Attack Multiplier cannot be empty!');
  }
  if (!updateObject.stats.defence_multiplier && updateObject.stats.defence_multiplier !== 0) {
    throw new Error('Defense Multiplier cannot be empty!');
  }
  if (!updateObject.stats.health_multiplier && updateObject.stats.health_multiplier !== 0) {
    throw new Error('Health Multiplier cannot be empty!');
  }
  if (!updateObject.stats.speed_multiplier && updateObject.stats.speed_multiplier !== 0) {
    throw new Error('Speed Multiplier cannot be empty!');
  }
  if (!updateObject.stats.critical_chance_multiplier && updateObject.stats.critical_chance_multiplier !== 0) {
    throw new Error('Crit Multiplier cannot be empty!');
  }
};

const ItemEditor = () => {
  const { setErrorData } = useContext(AppContext);
  const [items, setItems] = useState([]);
  const { basejumpToken } = useTokenAuth();
  const [selectedItem, setSelectedItem] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [newAttackPoints, setAttackPoints] = useState('');
  const [newDefensePoints, setDefensePoints] = useState('');
  const [newHealthPoints, setHealthPoints] = useState('');
  const [newAttackMult, setAttackMult] = useState('');
  const [newDefenseMult, setDefenseMult] = useState('');
  const [newHealthMult, setHealthMult] = useState('');
  const [newSpeedPoints, setSpeedPoints] = useState('');
  const [newCriticalChancePoints, setCriticalChancePoints] = useState('');
  const [newSpeedMult, setSpeedMult] = useState('');
  const [newCriticalChanceMult, setCriticalChanceMult] = useState('');
	
  const fetchItems = async () => {
    const itemsFromApi = await getEditorItems(basejumpToken);
    const assets = itemsFromApi.map(i => (AssetModel.fromBasejumpEquipment({id: i.id, item: i})));
    setItems(assets);
  };

  useEffect(() => {
    fetchItems();
  }, [basejumpToken]);

  useEffect(() => {
    if (selectedItem) {
      setName(selectedItem.getName())
      setDescription(selectedItem.getDescription())
      const {
        attackPoints,
        defencePoints,
        healthPoints,
        attackMultiplier,
        defenceMultiplier,
        healthMultiplier,
        criticalChancePoints,
        criticalChanceMultiplier,
        speedPoints,
        speedMultiplier
      } = selectedItem.getStats();
      setAttackPoints(`${attackPoints}`);
      setDefensePoints(`${defencePoints}`);
      setHealthPoints(`${healthPoints}`);
      setAttackMult(`${attackMultiplier}`);
      setDefenseMult(`${defenceMultiplier}`);
      setHealthMult(`${healthMultiplier}`);
      setSpeedPoints(`${speedPoints}`);
      setCriticalChancePoints(`${criticalChancePoints}`);
      setSpeedMult(`${speedMultiplier}`);
      setCriticalChanceMult(`${criticalChanceMultiplier}`);
    }
  }, [selectedItem]);
  
  const controls = {};
  const model = useMemo(() => ({
    id: '0000',
    name: 'mock',
    vrm: '/assets/default_avatar.glb',
    glb: '/assets/default_avatar.vrm',
    image: '/assets/avatar.png'
  }), []);

  const onSaveSelectedItem = async () => {
    setSaveDisabled(true);
    try {
      const updateObject = {
        name,
        description,
        stats: {
          attack_points: parseFloat(newAttackPoints),
          defence_points: parseFloat(newDefensePoints),
          health_points: parseFloat(newHealthPoints),
          attack_multiplier: parseFloat(newAttackMult),
          defence_multiplier: parseFloat(newDefenseMult),
          health_multiplier: parseFloat(newHealthMult),
          speed_points: parseFloat(newSpeedPoints),
          critical_chance_points: parseFloat(newCriticalChancePoints),
          speed_multiplier: parseFloat(newSpeedMult),
          critical_chance_multiplier: parseFloat(newCriticalChanceMult),
	}
      };
      validateUpdateObject(updateObject);
      const updatedItem = await updateEditorItem(basejumpToken, selectedItem.getItemId(), updateObject);
      const itemsFromApi = await getEditorItems(basejumpToken);
      const assets = itemsFromApi.map(i => (AssetModel.fromBasejumpEquipment({id: i.id, item: i})));
      setItems(assets);
    } catch (err) {
      setErrorData({ title: 'Error', description: err.message });
    } finally {
      setSaveDisabled(false);
    }
  };

  return (
    <EditorContent>
      <MintFigureView controls={controls}
        figure={FigureModel.fromAlchemyNft(model)}
        noDecorators
        noEquip
        customEquip={selectedItem ? [selectedItem] : null}
        width='20rem'
        height='100%'
        maxHeight={'100%'}
      />
      <ItemDataSection>
        {selectedItem && <>
          <ItemAssets>
            <ItemAsset>
              <AssetView key={`asset-editor-${selectedItem.getItemId()}`} asset={selectedItem}/>
            </ItemAsset>
            <ItemAsset>
              <img src={selectedItem.getImgSource()} />
            </ItemAsset>
          </ItemAssets>
          <ItemParams key={`params-editor-${selectedItem.getItemId()}`}>
            <ItemParam>
              <EditorLabel>Name:</EditorLabel>
              <TextInput initText={selectedItem.getName()} onChange={(newName) => setName(newName)}/>
              <EditorLabel>Description:</EditorLabel>
              <TextInputMultiline initText={selectedItem.getDescription()} onChange={(newDescription) => setDescription(newDescription)} placeholder=''/>
              <IconButton onClick={onSaveSelectedItem} disabled={saveDisabled || !selectedItem}>
                Save changes
              </IconButton>
            </ItemParam>
            <ItemParam>
              <ItemParams>
                <ItemParam>
                  <EditorLabel>Attack Points:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().attackPoints}`} onChange={(newPoints) => setAttackPoints(newPoints)}/>
                  <EditorLabel>Defense Points:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().defencePoints}`} onChange={(newPoints) => setDefensePoints(newPoints)}/>
                  <EditorLabel>Health Points:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().healthPoints}`} onChange={(newPoints) => setHealthPoints(newPoints)}/>
                  <EditorLabel>Speed Points:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().speedPoints}`} onChange={(newPoints) => setSpeedPoints(newPoints)}/>
                  <EditorLabel>Crit Points:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().criticalChancePoints}`} onChange={(newPoints) => setCriticalChancePoints(newPoints)}/>
                </ItemParam>
                <ItemParam>
                  <EditorLabel>Attack Multiplier:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().attackMultiplier}`} onChange={(newPoints) => setAttackMult(newPoints)}/>
                  <EditorLabel>Defense Multiplier:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().defenceMultiplier}`} onChange={(newPoints) => setDefenseMult(newPoints)}/>
                  <EditorLabel>Health Multiplier:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().healthMultiplier}`} onChange={(newPoints) => setHealthMult(newPoints)}/>
                  <EditorLabel>Speed Multiplier:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().speedMultiplier}`} onChange={(newPoints) => setSpeedMult(newPoints)}/>
                  <EditorLabel>Crit Multiplier:</EditorLabel>
                  <TextInput initText={`${selectedItem.getStats().criticalChanceMultiplier}`} onChange={(newPoints) => setCriticalChanceMult(newPoints)}/>
                </ItemParam>
              </ItemParams>
            </ItemParam>
          </ItemParams>
        </>}
      </ItemDataSection>
      <AllItemList items={items} selectedItem={selectedItem} onSelectItem={(it) => setSelectedItem(it)}/>
    </EditorContent>
  );
};

const ItemEditorPopup = () => {
  const categories = {
    editor: {
      name: 'Item Editor',
      component: (<ItemEditor/>)
    },
    metrics: {
      name: 'Metrics',
      component: (<Metrics/>)
    },
    npc: {
      name: 'Npc',
      component: (<NpcEditor/>)
    }
  };
  const [selectedCategory, setSelectedCategory] = useState('editor');

  return (
    <BroadsiderHudPopup overwrightWidth='75vw'>
      <StyledItemEditor>
        <TabHeader>
          {Object.keys(categories).map((k) => <Tab isSelected={selectedCategory === k} onSelect={() => setSelectedCategory(k)}>{categories[k].name}</Tab>)}
        </TabHeader>
        {categories[selectedCategory].component}
      </StyledItemEditor>
    </BroadsiderHudPopup>
  );
};

export default ItemEditorPopup;
